// libs
import {
  MeetingFragmentFragment,
  useCreateMeetingReportMutation,
  useUpdateMeetingReportMutation
} from '@lopo-tech/lopo-api'
import React, { useEffect, useState } from 'react'
import { CLASS_PREFIX } from 'src/constants'

import { Button } from 'src/components/button/button'
import { textResources } from 'src/lang/de'
import { ReportFormInput } from './report_form_input'

import { showSnackBar } from 'src/components/snack_bar/snack_bar'
import './report_form.scss'

type Props = Pick<
  MeetingFragmentFragment,
  'id' | 'canCreateReport' | 'reportFieldDefinitions' | 'report'
> & { closeModal: () => void };

const cls = `${CLASS_PREFIX}report-form`

export const ReportForm: React.FC<Props> = ({
  id: meetingId,
  canCreateReport,
  reportFieldDefinitions,
  report,
  closeModal
}) => {
  const [fieldValues, setFieldValues] = useState<
    Record<string, string | undefined>
  >({})
  const [fieldErrors, setFieldErrors] = useState<
    Record<string, string | undefined>
  >({})
  const [loading, setLoading] = useState(false)

  const [createMeetingReport] = useCreateMeetingReportMutation()
  const [updateMeetingReport] = useUpdateMeetingReportMutation()

  const validateForm = () => {
    let valid = true

    reportFieldDefinitions.forEach((field) => {
      if (field.required && !fieldValues[field.key]) {
        valid = false
        setFieldErrors((prev) => {
          return {
            ...prev,
            [field.key]: textResources.shared.required
          }
        })
      }
    })

    return valid
  }

  useEffect(() => {
    if (report) {
      const initialValues: Record<string, string> = {}
      report.fieldValues.forEach((field) => {
        initialValues[field.key] = field.value ?? ''
      })
      setFieldValues(initialValues)
    } else {
      setFieldValues({})
    }
  }, [report, reportFieldDefinitions])

  if (!report && !canCreateReport.value) {
    // this should not be reachable in that case
    return null
  }

  const disabled = !!report && !report.canUpdate.value

  const inputsToShow =
    (report ? report.fieldValues : reportFieldDefinitions)?.filter(
      (field) => field.visible
    ) ?? []

  const title = report
    ? report.canUpdate.value
      ? textResources.projects.meetingReport.editReport
      : textResources.projects.meetingReport.report
    : textResources.projects.meetingReport.createReport

  return (
    <div className={cls}>
      <div className={`${cls}__header`}>
        {title}
      </div>
      <div className={`${cls}__form-fields`}>
        {inputsToShow.map((field) => {
          return (
            <ReportFormInput
              {...field}
              key={field.key}
              name={field.key}
              value={fieldValues[field.key]}
              errorText={fieldErrors[field.key]}
              disabled={disabled}
              setValue={(value) => {
                setFieldErrors((prev) => ({
                  ...prev,
                  [field.key]: undefined
                }))
                setFieldValues((prev) => ({ ...prev, [field.key]: value }))
              }}
            />
          )
        })}
      </div>
      {!disabled && (
        <Button
          disabled={loading}
          onClick={async () => {
            setLoading(true)
            if (validateForm()) {
              if (canCreateReport.value && !report) {
                const result = await createMeetingReport({
                  variables: { input: { meetingId, fieldValues } }
                })

                if (result.data?.createMeetingReport?.meetingReport) {
                  closeModal()
                  setFieldValues({})
                } else {
                  showSnackBar(textResources.shared.errorUnknown)
                }
              } else if (report) {
                const result = await updateMeetingReport({
                  variables: { input: { id: report.id, fieldValues } }
                })

                if (result.data?.updateMeetingReport?.meetingReport) {
                  closeModal()
                  setFieldValues({})
                } else {
                  showSnackBar(textResources.shared.errorUnknown)
                }
              }
            }
            setLoading(false)
          }}
          title={report ? textResources.projects.meetingReport.saveChanges : textResources.projects.meetingReport.create}
        />
      )}
    </div>
  )
}
